import React from "react";
import { CherryPayApi } from "../../api/models";
import { DateField } from "../DateField/DateField";
import { DateTimeField } from "../DateTimeField/DateTimeField";
import { ChakraReactSelectField } from "../fields/SelectField/ChakraReactSelectField";
import { IntegerField } from "../fields/IntegerField/IntegerField";
import { TextField } from "../fields/TextField/TextField";
import { TextArea } from "../fields/TextArea/TextArea";
import { SwitchField } from "../fields/SwitchField/SwitchField";
import { Text } from "@chakra-ui/react";

interface ExtendedFieldInputProps {
  fieldDefinition: CherryPayApi.ExtendedFieldDefinition;
  fieldName: string;
  isDisabled?: boolean;
}

export const ExtendedFieldInput: React.FC<ExtendedFieldInputProps> = ({
  fieldDefinition,
  fieldName,
  isDisabled = false,
}) => {
  const {
    FieldName,
    Description,
    FieldType,
    Writability,
    NumberAttributes,
    StringAttributes,
    DateAttributes,
    BooleanAttributes,
    TimeAttributes,
  } = fieldDefinition;

  // Determine if field should be read-only
  const isReadOnly =
    isDisabled ||
    Writability === "ReadOnly" ||
    Writability === "SystemWritable";

  // Determine if field is required by checking the type-specific attributes
  const isRequired =
    BooleanAttributes?.IsRequired ||
    DateAttributes?.IsRequired ||
    NumberAttributes?.IsRequired ||
    StringAttributes?.IsRequired ||
    TimeAttributes?.IsRequired ||
    false;

  // Common props for all fields
  const commonProps = {
    name: fieldName,
    label: FieldName || fieldName,
    isDisabled: isReadOnly,
    infoText: Description || undefined,
    isRequired: isRequired,
  };

  switch (FieldType) {
    case "Date":
      return (
        <DateField
          {...commonProps}
          dateOnly={true}
          min={DateAttributes?.MinValue ?? undefined}
          max={DateAttributes?.MaxValue ?? undefined}
        />
      );

    case "DateTime":
      return <DateTimeField {...commonProps} timezoneAgnostic={true} />;

    case "Time":
      // Use a time input for Time type
      return <TextField {...commonProps} type="time" />;

    case "Number":
      return (
        <IntegerField
          {...commonProps}
          min={NumberAttributes?.MinValue ?? undefined}
          max={NumberAttributes?.MaxValue ?? undefined}
          precision={NumberAttributes?.DecimalPlaces ?? 0}
          step={
            NumberAttributes?.DecimalPlaces
              ? Math.pow(10, -NumberAttributes.DecimalPlaces)
              : 1
          }
        />
      );

    case "NumberSelection":
      // Number field with allowed values - render as select
      if (
        NumberAttributes?.AllowedValues &&
        NumberAttributes.AllowedValues.length > 0
      ) {
        const options = NumberAttributes.AllowedValues.map((value) => ({
          label: value.toString(),
          value: value.toString(),
        }));
        return (
          <ChakraReactSelectField
            {...commonProps}
            options={options}
            allowEmpty={true}
          />
        );
      }
      // Fallback to regular number field if no allowed values
      return (
        <IntegerField
          {...commonProps}
          min={NumberAttributes?.MinValue ?? undefined}
          max={NumberAttributes?.MaxValue ?? undefined}
          precision={NumberAttributes?.DecimalPlaces ?? 0}
          step={
            NumberAttributes?.DecimalPlaces
              ? Math.pow(10, -NumberAttributes.DecimalPlaces)
              : 1
          }
        />
      );

    case "NumberMultiSelection":
      // Number field with multiple values allowed - render as multi-select
      if (
        NumberAttributes?.AllowedValues &&
        NumberAttributes.AllowedValues.length > 0
      ) {
        const options = NumberAttributes.AllowedValues.map((value) => ({
          label: value.toString(),
          value: value.toString(),
        }));
        return (
          <ChakraReactSelectField
            {...commonProps}
            options={options}
            isMulti={true}
            allowEmpty={false}
          />
        );
      }
      // Fallback to regular number field if no allowed values
      return (
        <IntegerField
          {...commonProps}
          min={NumberAttributes?.MinValue ?? undefined}
          max={NumberAttributes?.MaxValue ?? undefined}
          precision={NumberAttributes?.DecimalPlaces ?? 0}
          step={
            NumberAttributes?.DecimalPlaces
              ? Math.pow(10, -NumberAttributes.DecimalPlaces)
              : 1
          }
        />
      );

    case "TextArea":
      // Always render as textarea for TextArea type
      return (
        <TextArea
          {...commonProps}
          minLength={StringAttributes?.MinLength ?? undefined}
          maxLength={StringAttributes?.MaxLength ?? undefined}
        />
      );

    case "Text":
      // Check if it should be a text area (no specific indicator, using length as heuristic)
      const shouldUseTextArea = (StringAttributes?.MaxLength ?? 0) > 200;

      if (shouldUseTextArea) {
        return (
          <TextArea
            {...commonProps}
            minLength={StringAttributes?.MinLength ?? undefined}
            maxLength={StringAttributes?.MaxLength ?? undefined}
          />
        );
      }

      return (
        <TextField
          {...commonProps}
          minLength={StringAttributes?.MinLength ?? undefined}
          maxLength={StringAttributes?.MaxLength ?? undefined}
        />
      );

    case "TextSelection":
      // Text field with allowed values - render as select
      if (
        StringAttributes?.AllowedValues &&
        StringAttributes.AllowedValues.length > 0
      ) {
        const options = StringAttributes.AllowedValues.map((value) => ({
          label: value,
          value: value,
        }));
        return (
          <ChakraReactSelectField
            {...commonProps}
            options={options}
            allowEmpty={true}
          />
        );
      }
      // Fallback to regular text field if no allowed values
      return (
        <TextField
          {...commonProps}
          minLength={StringAttributes?.MinLength ?? undefined}
          maxLength={StringAttributes?.MaxLength ?? undefined}
        />
      );

    case "TextMultiSelection":
      // Text field with multiple values allowed - render as multi-select
      if (
        StringAttributes?.AllowedValues &&
        StringAttributes.AllowedValues.length > 0
      ) {
        const options = StringAttributes.AllowedValues.map((value) => ({
          label: value,
          value: value,
        }));
        return (
          <ChakraReactSelectField
            {...commonProps}
            options={options}
            isMulti={true}
            allowEmpty={false}
          />
        );
      }
      // Fallback to regular text field if no allowed values
      return (
        <TextField
          {...commonProps}
          minLength={StringAttributes?.MinLength ?? undefined}
          maxLength={StringAttributes?.MaxLength ?? undefined}
        />
      );

    case "Toggle":
      return <SwitchField {...commonProps} />;

    default:
      // Unknown field type - render as plain text or skip
      return (
        <Text fontSize="sm" color="gray.500">
          Unknown field type: {FieldType}
        </Text>
      );
  }
};
